// @flow
import React from "react";
import classNames from "classnames";
import withStyles from "elevate-ui/withStyles";
import Typography from "elevate-ui/Typography";
import Phone from "elevate-ui-icons/Phone";
import Print from "elevate-ui-icons/Print";
import Email from "elevate-ui-icons/Email";
import Link from "elevate-ui-icons/Link";

import { withGlobalContext } from "../common/GlobalContext";

import type { $Agent, ComplianceItems } from "flow/types";

type Props = {
  key: string,
  classes: Object,
  className: string,
  agent: $Agent,
  compliance: ComplianceItems,
};

const AgentCard = ({ classes, className, agent, compliance, key }: Props) => {
  return (
    <div className={classNames(classes.root, className)} key={key}>
      {agent.slug && agent.slug.length ? (
        <a className={classes.photo} href={`${agent.slug}`}>
          {agent.picture && (
            <img
              className={classes.photo}
              src={agent.picture}
              alt={agent.name}
            />
          )}
        </a>
      ) : agent.picture ? (
        <div className={classes.photo}>
          <img
            className={classes.photo}
            src={`${agent.picture}`}
            alt={agent.name}
          />
        </div>
      ) : null}
      <div className={classes.details}>
        <div className={classes.detailsTop}>
          {agent.slug && agent.slug.length ? (
            <a href={`${agent.slug}`}>
              <div className={classes.name}>{agent.name}</div>
            </a>
          ) : (
            <div className={classes.name}>{agent.name}</div>
          )}
          {agent.jobTitle && (
            <Typography type="subHeading2" className={classes.jobTitle}>
              {agent.jobTitle}
            </Typography>
          )}
        </div>
        <div className={classes.detailsBottom}>
          {agent.licenseNumber &&
            compliance &&
            compliance.agentOptions &&
            compliance.agentOptions.dreNumberOnAgentCard && (
              <div className={classes.agentDetail}>
                <span
                  className={classNames(
                    classes.agentDetailText,
                    "ref__agent-card-drenumber"
                  )}
                >
                  DRE: {agent.licenseNumber}
                </span>
              </div>
            )}
          {agent.cellPhoneFormatted && (
            <a
              href={`tel:${agent.cellPhoneFormatted}`}
              className={classes.agentDetail}
            >
              <Phone size={18} className={classes.agentDetailIcon} />
              <span className={classes.agentDetailText}>
                Cell: {agent.cellPhoneFormatted}
              </span>
            </a>
          )}
          {agent.workPhone && agent.workPhoneFormatted ? (
            <a href={`tel:${agent.workPhone}`} className={classes.agentDetail}>
              <Phone size={18} className={classes.agentDetailIcon} />
              <span className={classes.agentDetailText}>
                Work: {agent.workPhoneFormatted}
              </span>
            </a>
          ) : null}
          {agent.directPhone && (
            <a
              href={`tel:${agent.directPhone}`}
              className={classes.agentDetail}
            >
              <Phone size={18} className={classes.agentDetailIcon} />
              <span className={classes.agentDetailText}>
                Direct: {agent.directPhoneFormatted}
              </span>
            </a>
          )}
          {agent.faxPhone && (
            <a href={`tel:${agent.faxPhone}`} className={classes.agentDetail}>
              <Print size={18} className={classes.agentDetailIcon} />
              <span className={classes.agentDetailText}>
                Fax: {agent.faxPhoneFormatted}
              </span>
            </a>
          )}
          {agent.email && (
            <a href={`mailto:${agent.email}`} className={classes.agentDetail}>
              <Email size={18} className={classes.agentDetailIcon} />
              <span className={classes.agentDetailText}>{agent.email}</span>
            </a>
          )}
          {agent.elevateSiteUrl ? (
            <a
              className={classes.agentDetail}
              href={agent.elevateSiteHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Link size={18} className={classes.agentDetailIcon} />
              <span className={classes.agentDetailText}>
                {agent.elevateSiteUrl}
              </span>
            </a>
          ) : agent.mlsWebsiteUrl ? (
            <a
              className={classes.agentDetail}
              href={agent.mlsWebsiteHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Link size={18} className={classes.agentDetailIcon} />
              <span className={classes.agentDetailText}>
                {agent.mlsWebsiteUrl}
              </span>
            </a>
          ) : null}
          {agent.otherWebsiteUrl ? (
            <a
              className={classes.agentDetail}
              href={agent.otherWebsiteHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Link size={18} className={classes.agentDetailIcon} />
              <span className={classes.agentDetailText}>
                {agent.otherWebsiteUrl}
              </span>
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default withStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "280px",
    background: "#fff",
    borderRadius: theme.globalBorderRadius,
    boxShadow:
      "0 2px 1px -1px rgba(0,0,0,.2), 0 1px 1px 0 rgba(0,0,0,.14), 0 1px 3px 0 rgba(0,0,0,.12)", // material-design 1dp
    transition: "all 200ms ease-in-out",
    overflow: "hidden",
  },
  photo: {
    width: "280px",
    height: "280px",
    objectFit: "contain",
    position: "relative",
  },
  details: {
    padding: "12px 10px 12px 12px",
    width: "100%",
  },
  detailsTop: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginBottom: theme.spacing.unit * 0.75,
  },
  detailsBottom: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  name: {
    color: theme.colors.gray["900"],
    fontSize: "18px",
    lineHeight: "1.2",
    fontWeight: "700",
  },
  jobTitle: {
    marginTop: theme.spacing.unit * 0.25,
  },
  agentDetail: {
    color: theme.colors.gray["800"],
    fontSize: "14px",
    lineHeight: "1.2",
    fontWeight: "600",
    marginBottom: theme.spacing.unit * 0.5,
    display: "flex",
    alignItems: "center",
  },
  agentDetailIcon: {
    flexShrink: "0",
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing.unit * 0.5,
    fill: theme.colors.gray["800"],
  },
  agentDetailText: {
    width: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}))(withGlobalContext(AgentCard));
